import React from "react";
import { Check, ChevronRight, ChevronsUpDown, Copy } from "lucide-react";
import { DataTableColumn } from "../../../shared/ui/DataTable";
import { TransactionStatusBadge } from "./TransactionStatusBadge";
import { ProviderBadge } from "./ProviderBadge";
import { CustomerCell } from "./CustomerCell";
import { TransactionItem } from "../types";
import { mapTransactionToOtcRequest } from "./details/mapTransactionToOtcRequest";

export interface CreateTransactionColumnsParams {
  copiedId: string | null;
  onCopyId: (e: React.MouseEvent, id: string) => void;
  isOtcTab?: boolean;
}

export const formatTransactionDate = (dateInput?: string | Date): string => {
  if (!dateInput) return "N/A";
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return String(dateInput);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatTransactionDateTime = (dateInput?: string | Date): string => {
  if (!dateInput) return "N/A";
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return String(dateInput);
  const formattedDate = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const formattedTime = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${formattedDate} • ${formattedTime}`;
};

export const createTransactionColumns = ({
  copiedId,
  onCopyId,
  isOtcTab = false,
}: CreateTransactionColumnsParams): Array<DataTableColumn<TransactionItem>> => {
  if (isOtcTab) {
    return [
      {
        id: "dateRequested",
        header: (
          <div className="flex items-center gap-1.5 cursor-pointer select-none">
            <span>Date requested</span>
            <ChevronsUpDown size={13} className="text-[#98A2B3]" />
          </div>
        ),
        headerClassName: "py-2.5 px-3 text-[12px] font-medium text-[#475467]",
        cellClassName: "py-2.5 px-3 text-[13px] font-normal text-[#475467] whitespace-nowrap",
        cell: (transaction) => {
          const dateVal =
            transaction.otc?.createdAt ||
            transaction.createdAt ||
            transaction.transactionDate;
          return formatTransactionDateTime(dateVal);
        },
      },
      {
        id: "amount",
        header: "Amount",
        headerClassName: "py-2.5 px-3 text-[12px] font-medium text-[#475467]",
        cellClassName: "py-2.5 px-3 whitespace-nowrap",
        cell: (transaction) => {
          const rawAmount = Number(transaction.otc?.amount ?? transaction.amount) || 0;
          const absAmount = Math.abs(rawAmount);
          const currency = (transaction.otc?.currency || transaction.currency || "USD").toUpperCase();

          const formattedNum = absAmount.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });

          let symbol = "$";
          if (currency === "NGN") symbol = "₦";
          else if (currency === "GBP") symbol = "£";
          else if (currency === "EUR") symbol = "€";
          else if (currency === "USD") symbol = "$";

          return (
            <span className="text-[13px] font-semibold text-[#101828] whitespace-nowrap">
              {symbol}{formattedNum}
            </span>
          );
        },
      },
      {
        id: "requestedRate",
        header: "Requested rate",
        headerClassName: "py-2.5 px-3 text-[12px] font-medium text-[#475467]",
        cellClassName: "py-2.5 px-3 text-[13px] font-normal text-[#344054] whitespace-nowrap",
        cell: (transaction) => {
          const otcRequest = mapTransactionToOtcRequest(transaction);
          return otcRequest.requestedRate || "-";
        },
      },
      {
        id: "currencyPair",
        header: "Currency pair",
        headerClassName: "py-2.5 px-3 text-[12px] font-medium text-[#475467]",
        cellClassName: "py-2.5 px-3 text-[13px] font-normal text-[#344054] whitespace-nowrap",
        cell: (transaction) => {
          const sourceCur = (transaction.otc?.currency || transaction.currency || "USD").toUpperCase();
          const targetCur = (transaction.otc?.currencyTo || transaction.currencyTo || "NGN").toUpperCase();
          return `${sourceCur} - ${targetCur}`;
        },
      },
      {
        id: "customer",
        header: "Customer",
        headerClassName: "py-2.5 px-3 text-[12px] font-medium text-[#475467]",
        cellClassName: "py-2.5 px-3 whitespace-nowrap",
        cell: (transaction) => {
          const customerName =
            transaction.business?.name ||
            transaction.customer?.name ||
            transaction.accountName ||
            "TradeVu User";
          return <CustomerCell name={customerName} />;
        },
      },
      {
        id: "transactionId",
        header: "Transaction ID",
        headerClassName: "py-2.5 px-3 text-[12px] font-medium text-[#475467]",
        cellClassName: "py-2.5 px-3 text-[13px] whitespace-nowrap",
        cell: (transaction) => {
          const refId =
            transaction.TransactionReference ||
            transaction.RequestReference ||
            transaction._id ||
            "";
          const displayId =
            refId.length > 12
              ? `${refId.slice(0, 5)}...${refId.slice(-4)}`
              : refId;

          return (
            <div className="flex items-center justify-between gap-3 text-[13px] text-[#344054]">
              <div className="flex items-center gap-1.5">
                <span>{displayId}</span>
                <button
                  type="button"
                  onClick={(e) => onCopyId(e, refId)}
                  className="rounded p-1 text-[#667085] transition-colors hover:bg-[#EEF1FB] hover:text-[#101828]"
                  title="Copy ID"
                >
                  {copiedId === refId ? (
                    <Check size={14} className="text-[#12B76A]" />
                  ) : (
                    <Copy size={14} />
                  )}
                </button>
              </div>
              <ChevronRight
                size={18}
                className="text-[#98A2B3] opacity-0 group-hover:opacity-100 transition-opacity ml-auto shrink-0"
              />
            </div>
          );
        },
      },
    ];
  }

  return [
  {
    id: "date",
    header: "Date",
    headerClassName: "py-3 px-4 text-[12px] font-medium text-[#475467]",
    cellClassName: "py-3 px-4 text-[13px] font-normal text-[#475467] whitespace-nowrap",
    cell: (trx) =>
      formatTransactionDateTime(
        trx.transactionDate || trx.createdAt || trx.updatedAt
      ),
  },
  {
    id: "amount",
    header: "Amount",
    headerClassName: "py-3 px-4 text-[12px] font-medium text-[#475467]",
    cellClassName: "py-3 px-4 whitespace-nowrap",
    cell: (trx) => {
      const isDebit =
        trx.PaymentType?.toLowerCase() === "debit" || Number(trx.amount) < 0;
      const rawAmount = Number(trx.amount) || 0;
      const absAmount = Math.abs(rawAmount);
      const currency = trx.currency?.toUpperCase() || "NGN";

      const formattedNum = absAmount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const isCrypto = currency === "USDT" || currency === "USDC";

      let symbol = "";
      if (currency === "USD") symbol = "$";
      else if (currency === "GBP") symbol = "£";
      else if (currency === "EUR") symbol = "€";
      else if (currency === "NGN") symbol = "₦";

      const sign = isDebit
        ? symbol === "£" || symbol === "$"
          ? "-"
          : "- "
        : "";

      const showSuffix = !trx.exchangeSubAmount || isCrypto;

      return (
        <div className="flex flex-col justify-center">
          <span
            className={`text-[13px] font-semibold whitespace-nowrap ${
              isDebit ? "text-[#101828]" : "text-[#12B76A]"
            }`}
          >
            {sign}
            {symbol}
            {formattedNum}
            {showSuffix && (
              <span className="ml-1 text-[13px] font-normal text-[#667085]">
                {currency}
              </span>
            )}
          </span>

          {trx.exchangeSubAmount && (
            <span className="text-[11px] font-normal text-[#667085] whitespace-nowrap">
              {trx.exchangeSubAmount}
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: "revenue",
    header: "Revenue",
    headerClassName: "py-3 px-4 text-[12px] font-medium text-[#475467]",
    cellClassName: "py-3 px-4 text-[13px] font-normal text-[#344054] whitespace-nowrap",
    cell: (trx) => {
      if (trx.revenue) return trx.revenue;
      const fee = trx.fee ?? trx.transactionFee;
      if (fee !== undefined && fee !== null && fee !== "") {
        return `₦${fee}`;
      }
      return "N/A";
    },
  },
  {
    id: "customer",
    header: "Customer",
    headerClassName: "py-3 px-4 text-[12px] font-medium text-[#475467]",
    cellClassName: "py-3 px-4 whitespace-nowrap",
    cell: (trx) => {
      const customerName =
        trx.customer?.name || trx.business?.name || "TradeVu User";
      return <CustomerCell name={customerName} />;
    },
  },
  {
    id: "counterparty",
    header: "Counterparty",
    headerClassName: "py-3 px-4 text-[12px] font-medium text-[#475467]",
    cellClassName: "py-3 px-4 text-[13px] font-normal text-[#344054] whitespace-nowrap",
    cell: (trx) => {
      const counterpartyName =
        trx.counterparty || trx.accountName || "N/A";
      return (
        <div className="max-w-[105px] truncate" title={counterpartyName}>
          {counterpartyName}
        </div>
      );
    },
  },
  {
    id: "status",
    header: "Status",
    headerClassName: "py-3 px-4 text-[12px] font-medium text-[#475467]",
    cellClassName: "py-3 px-4 whitespace-nowrap",
    cell: (trx) => {
      const resolvedStatus = trx.isFlagged
        ? "Flagged"
        : trx.status || trx.otc?.status || "";
      return <TransactionStatusBadge status={resolvedStatus} />;
    },
  },
  {
    id: "provider",
    header: "Provider",
    headerClassName: "py-3 px-4 text-[12px] font-medium text-[#475467]",
    cellClassName: "py-3 px-4 whitespace-nowrap",
    cell: (trx) => <ProviderBadge provider={trx.provider || "N/A"} />,
  },
  {
    id: "transactionId",
    header: "Transaction ID",
    headerClassName: "py-3 px-4 text-[12px] font-medium text-[#475467]",
    cellClassName: "py-3 px-4 text-[13px] whitespace-nowrap",
    cell: (trx) => {
      const refId =
        trx.TransactionReference ||
        trx.RequestReference ||
        trx._id ||
        "";
      const displayId =
        refId.length > 12
          ? `${refId.slice(0, 5)}...${refId.slice(-4)}`
          : refId;

      return (
        <div className="flex items-center justify-between gap-3 text-[13px] text-[#344054]">
          <div className="flex items-center gap-1.5">
            <span>{displayId}</span>
            <button
              type="button"
              onClick={(e) => onCopyId(e, refId)}
              className="rounded p-1 text-[#667085] transition-colors hover:bg-[#EEF1FB] hover:text-[#101828]"
              title="Copy ID"
            >
              {copiedId === refId ? (
                <Check size={14} className="text-[#12B76A]" />
              ) : (
                <Copy size={14} />
              )}
            </button>
          </div>
          <ChevronRight
            size={18}
            className="text-[#98A2B3] opacity-0 group-hover:opacity-100 transition-opacity ml-auto shrink-0"
          />
        </div>
      );
    },
  },
];
};
